import React, { useCallback } from 'react';

import { useNarrativeTextInput } from '../../hooks/useNarrativeTextInput';
import { logger } from '../../lib/logger';
import {
  useCurrentNode,
  useNarrativeState,
  useUpdateNode,
  useUpdateNodeChoice,
} from '../../store/hooks';

import { ChoicesList } from './ChoicesList';
import { DynamicTextarea } from './DynamicTextarea';
import { SectionTitle } from './SectionTitle';

const endingLogger = logger.child('[EndingNodeEditor]');

export const EndingNodeEditor = React.memo(function EndingNodeEditor() {
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const updateNodeChoice = useUpdateNodeChoice();
  const currentNodeId = narrativeState.currentNodeId;

  const handleTextUpdate = useCallback(
    (value: string) => {
      updateNode(currentNodeId, { text: value });
    },
    [currentNodeId, updateNode]
  );

  const endingText = useNarrativeTextInput(currentNode?.text || '', handleTextUpdate, 300, 1000);

  const handleChoiceUpdate = useCallback(
    (choiceIndex: number, field: 'text' | 'npcResponse', value: string) => {
      updateNodeChoice(currentNodeId, choiceIndex, { [field]: value });
    },
    [currentNodeId, updateNodeChoice]
  );

  endingLogger.debug('Rendered');

  if (!currentNode) {
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="space-y-5">
        <div>
          <SectionTitle
            title="Text Leading Up to Choices"
            subtitle="Ending Narrative"
            text={endingText.value}
            showWordCount={true}
          />
          <DynamicTextarea
            value={endingText.value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              endingText.onChange(e.target.value)
            }
            className="w-full bg-gradient-to-br from-[#161c2e] to-[#1a2040] border border-[#2a3756] rounded-lg p-4 text-white focus:outline-none focus:border-[#4f46e5] focus:shadow-[0_0_20px_rgba(79,70,229,0.3)] focus:scale-[1.01] hover:border-[#06b6d4] transition-all duration-300 text-sm backdrop-blur-sm shadow-[0_4px_16px_rgba(0,0,0,0.2)]"
            placeholder="Describe the aftermath..."
            minRows={5}
            wordLimit={100}
          />
        </div>

        <ChoicesList
          choices={currentNode.choices}
          currentNodeId={currentNodeId}
          onChoiceUpdate={handleChoiceUpdate}
        />
      </div>
    </div>
  );
});
